import { Modal, Stack, Text, Textarea, Button, Group } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useWrapUp } from '../hooks/useWrapUp';

interface WrapUpModalProps {
  opened: boolean;
  onClose: () => void;
  storyId: string;
  sessionId: string;
  storyTitle: string;
  onSuccess?: () => void;
}

export function WrapUpModal({
  opened,
  onClose,
  storyId,
  sessionId,
  storyTitle,
  onSuccess,
}: WrapUpModalProps) {
  const wrapUp = useWrapUp();

  const form = useForm({
    initialValues: {
      reflection: '',
    },
    validate: {
      reflection: (value) => {
        if (!value || value.trim().length === 0) {
          return 'Please write a reflection';
        }
        if (value.length < 20) {
          return 'Reflection must be at least 20 characters';
        }
        if (value.length > 1000) {
          return 'Reflection must be less than 1000 characters';
        }
        return null;
      },
    },
  });

  const handleSubmit = form.onSubmit((values) => {
    wrapUp.mutate(
      {
        storyId,
        sessionId,
        reflection: values.reflection,
      },
      {
        onSuccess: () => {
          form.reset();
          onClose();
          onSuccess?.();
        },
      }
    );
  });

  const handleSkip = () => {
    form.reset();
    onClose();
    onSuccess?.();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Wrap Up Your Reading"
      size="lg"
      centered
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Great work completing "<strong>{storyTitle}</strong>"! Take a moment to reflect on
            what you learned.
          </Text>

          <Textarea
            {...form.getInputProps('reflection')}
            label="Your Reflection"
            description={`${form.values.reflection.length}/1000 characters`}
            placeholder="What did you learn from this story? What surprised you? How did the story make you feel? What questions do you have?"
            minRows={6}
            maxRows={10}
            autosize
          />

          <Group justify="space-between" mt="md">
            <Button variant="subtle" color="gray" onClick={handleSkip}>
              Skip for Now
            </Button>
            <Button variant="light" type="submit" loading={wrapUp.isPending}>
              Save Reflection
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
